/**
 * Objects API Endpoints
 * All object-related API operations (tasks, events, contacts, notes, etc.)
 */

import { http } from '@/lib/http'
import { etagKey } from '@/lib/etag'
import { env } from '@/lib/env'
import { getActiveWorkspaceId } from '@/lib/api/workspace-context'
import {
  CONVERSATION_SCOPE_ID_KEY,
  CONVERSATION_SCOPE_IDS_KEY,
  CONVERSATION_SCOPE_LINKS_KEY,
} from './objects.scope-keys'
import { transformObject, extractStringList, dedupeStringList } from './objects.transform'
import type {
  AnyObject,
  CreateObjectRequest,
  UpdateObjectRequest,
  PaginatedObjectsResponse,
} from './objects.types'

// Re-export moved symbols so the barrel surface (`export *` from this file)
// and `./objects` imports (tests, lib/api/index.ts) stay stable.
export type { ConversationScopeLink } from './objects.scope-keys'
export {
  CONVERSATION_SCOPE_ID_KEY,
  CONVERSATION_SCOPE_IDS_KEY,
  CONVERSATION_SCOPE_LINKS_KEY,
} from './objects.scope-keys'
export type {
  BaseObject,
  TaskObject,
  EventObject,
  ContactObject,
  NoteObject,
  PlaceObject,
  AnyObject,
  PaginatedObjectsResponse,
  CreateObjectRequest,
  UpdateObjectRequest,
  PageThumbnailMetadata,
  PageOgImageMetadata,
} from './objects.types'
export {
  ThumbnailUploadError,
  uploadPageListThumbnail,
  uploadPageOgImage,
  generateReportListThumbnail,
} from './objects.thumbnails'

function wsObjects(sub = ''): string {
  return `/workspaces/${getActiveWorkspaceId()}/objects${sub}`
}

function wsTyped(type: string, sub = ''): string {
  return `/workspaces/${getActiveWorkspaceId()}/${type}s${sub}`
}

/**
 * List all objects with optional filters
 */
export async function listObjects(
  authToken: string | null,
  options?: {
    limit?: number
    cursor?: string
    type?: string
    conversationId?: string
    query?: string
  }
) {
  const searchParams: Record<string, string> = {}
  if (options?.limit) searchParams.limit = options.limit.toString()
  if (options?.cursor) searchParams.cursor = options.cursor
  if (options?.conversationId) searchParams[CONVERSATION_SCOPE_IDS_KEY] = options.conversationId
  if (options?.query) searchParams.query = options.query

  const endpoint = options?.type ? wsTyped(options.type) : wsObjects()

  const response = await http.get<PaginatedObjectsResponse>(endpoint, {
    authToken,
    searchParams,
  })

  return response.data
}

/**
 * Get a single object by ID
 * Routes to type-specific endpoint: GET /{type}s/{objectId}
 */
export async function getObject(
  authToken: string | null,
  objectType: string,
  objectId: string
) {
  const path = wsTyped(objectType, `/${objectId}`)
  const response = await http.get<AnyObject>(path, { authToken })

  return {
    object: transformObject({ ...response.data, _etag: response.etag ?? undefined }, undefined, response.etag ?? undefined),
    etagKey: etagKey('GET', path.startsWith('http') ? path : new URL(path, env.apiBaseUrl).toString()),
  }
}

/**
 * Create a new object using type-specific endpoints
 * Routes to /tasks, /notes, /contacts, etc. based on object type
 */
export async function createObject(
  authToken: string | null,
  data: CreateObjectRequest
) {
  const endpoint = wsTyped(data.type)

  // Remove the 'type' field from payload as it's in the URL
  const { type, ...payload } = data

  console.log('[createObject] Endpoint:', endpoint, 'Payload:', payload);

  const response = await http.post<AnyObject>(endpoint, payload, {
    authToken,
    headers: {
      'Idempotency-Key': crypto.randomUUID(),
    },
  })

  console.log('[createObject] Response:', response.data);

  return transformObject({ ...response.data, _etag: response.etag ?? undefined }, undefined, response.etag ?? undefined)
}

/**
 * Update an existing object
 * Routes to type-specific endpoint: PATCH /{type}s/{objectId}
 */
export async function updateObject(
  authToken: string | null,
  objectType: string,
  objectId: string,
  data: UpdateObjectRequest,
  ifMatch?: string
) {
  const endpoint = wsTyped(objectType, `/${objectId}`)

  const response = await http.patch<AnyObject>(endpoint, data, {
    authToken,
    ifMatch,
  })

  return transformObject({ ...response.data, _etag: response.etag ?? undefined }, undefined, response.etag ?? undefined)
}

/**
 * Delete an object (permanent delete — see xavior-openapi.yaml).
 * Routes to type-specific endpoint: DELETE /{type}s/{objectId}
 */
export async function deleteObject(
  authToken: string | null,
  objectType: string,
  objectId: string,
  permanent = true
) {
  await http.delete(wsTyped(objectType, `/${objectId}${permanent ? '?permanent=true' : ''}`), {
    authToken,
  })
}

/**
 * Retry the outbound provider-calendar push for a single event (#3951).
 * Re-runs the push server-side (synchronously) and returns the re-stamped event.
 * On success the event's providerSyncState flips to 'synced'; on a repeated
 * failure it stays 'error' (with an updated providerSyncError). The caller reads
 * the returned object to decide whether to clear the "not synced" indicator.
 */
export async function retryEventSync(authToken: string | null, eventId: string) {
  const response = await http.post<AnyObject>(
    wsTyped('event', `/${eventId}/retry-sync`),
    {},
    { authToken },
  )
  return transformObject({ ...response.data, _etag: response.etag ?? undefined }, undefined, response.etag ?? undefined)
}

/**
 * Sync a single contact to a connected provider account (#4782). Pushes/links
 * exactly this contact to the given integration via the provider-agnostic
 * write-back path and returns the re-stamped contact (its attributes'
 * providerSyncedAt / pushStatus / externalId reflect the new linked state). The
 * server runs the push synchronously; on a missing-write-scope / provider error
 * it returns a non-2xx the caller surfaces to the user.
 */
export async function syncContactToProvider(
  authToken: string | null,
  contactId: string,
  integrationId: string,
) {
  const response = await http.post<AnyObject>(
    wsTyped('contact', `/${contactId}/sync-to-provider`),
    { integrationId },
    { authToken },
  )
  return transformObject({ ...response.data, _etag: response.etag ?? undefined }, undefined, response.etag ?? undefined)
}

/**
 * Create an object linked to a conversation.
 */
export async function createConversationObject(
  authToken: string | null,
  conversationId: string,
  data: Omit<CreateObjectRequest, 'conversationLinks'>
) {
  const existingConversationLinks = Array.isArray((data as any)[CONVERSATION_SCOPE_LINKS_KEY])
    ? (data as any)[CONVERSATION_SCOPE_LINKS_KEY]
    : []
  const existingModuleLinks = Array.isArray((data as any).moduleLinks) ? (data as any).moduleLinks : []
  const moduleIdsFromData = extractStringList((data as any).moduleIds)
  const normalizedModuleIds = dedupeStringList([
    ...existingModuleLinks.map((entry: any) => String(entry?.moduleId ?? '').trim()).filter(Boolean),
    ...moduleIdsFromData,
  ])

  const allConversationIds = dedupeStringList([
    ...existingConversationLinks
      .map((entry: any) => String(entry?.conversationId ?? '').trim())
      .filter(Boolean),
    conversationId,
  ])

  const payload: CreateObjectRequest = {
    ...(data as CreateObjectRequest),
    [CONVERSATION_SCOPE_LINKS_KEY]: allConversationIds.map((id) => ({
      [CONVERSATION_SCOPE_ID_KEY]: id,
    })),
    ...(normalizedModuleIds.length > 0
      ? { moduleLinks: normalizedModuleIds.map((id) => ({ moduleId: id })) }
      : {}),
  }

  delete (payload as any).moduleIds

  const created = await createObject(authToken, payload)
  return transformObject(created, conversationId, created._etag)
}

/**
 * Get all objects linked to a conversation.
 */
export async function getConversationObjects(
  authToken: string | null,
  conversationId: string,
  options?: {
    type?: string
    limit?: number
    cursor?: string
  }
) {
  if (options?.type) {
    const typed = await getObjectsByType(authToken, options.type, {
      conversationId,
      cursor: options.cursor,
      limit: options.limit,
    })
    return { ...typed, items: typed.items ?? [] }
  }

  const allTypes = ['task', 'event', 'contact', 'note', 'place', 'asset', 'journal', 'log', 'stat', 'message']
  const perTypeLimit = options?.limit
    ? Math.max(1, Math.ceil(options.limit / allTypes.length))
    : 50

  const results = await Promise.all(
    allTypes.map(async (type) => {
      try {
        return await getObjectsByType(authToken, type, { conversationId, limit: perTypeLimit })
      } catch {
        return { items: [] as AnyObject[] }
      }
    })
  )

  const merged = results.flatMap((entry) => entry.items ?? [])
  merged.sort((a: any, b: any) => {
    const left = String(a.updated_at || a.updatedAt || a.created_at || '')
    const right = String(b.updated_at || b.updatedAt || b.created_at || '')
    if (left === right) return 0
    return left > right ? -1 : 1
  })

  const capped = options?.limit ? merged.slice(0, options.limit) : merged
  return {
    items: capped,
    total: capped.length,
    nextCursor: undefined,
  }
}

/**
 * Get objects by type
 * Routes to type-specific endpoint: GET /{type}s (e.g., GET /tasks)
 * Supports query params: cursor, limit, status, query, conversationIds
 */
export async function getObjectsByType(
  authToken: string | null,
  type: string,
  options?: {
    conversationId?: string
    moduleId?: string
    cursor?: string
    limit?: number
    status?: string
    query?: string
    /**
     * Provider-integration id to scope a contacts list to that account's
     * address-book records (`contact_sources.source_type='contacts'`). Only
     * meaningful for `type==='contact'`; the backend gates the filter to
     * contacts and ignores it otherwise. See the multi-provider contacts
     * source switcher (redesign §3.4).
     */
    source?: string
    /**
     * Contacts only: when true, a module-scoped list ALSO includes contacts with
     * a provider source link (the synced address book), not just module-linked
     * ones. The contacts module sets this so its holistic + per-account views see
     * synced contacts (which inbound sync never module-links). See redesign §3.4.
     */
    includeSourceLinked?: boolean
  }
) {
  const endpoint = wsTyped(type)
  const searchParams: Record<string, string> = {}

  if (options?.cursor) searchParams.cursor = options.cursor
  if (options?.limit) searchParams.limit = options.limit.toString()
  if (options?.status) searchParams.status = options.status
  if (options?.query) searchParams.query = options.query
  if (options?.source) searchParams.source = options.source
  if (options?.includeSourceLinked) searchParams.includeSourceLinked = 'true'
  // Prefer module-scoped queries over conversation-scoped
  if (options?.moduleId) {
    searchParams.moduleIds = options.moduleId
  } else if (options?.conversationId) {
    searchParams[CONVERSATION_SCOPE_IDS_KEY] = options.conversationId
  }

  const response = await http.get<PaginatedObjectsResponse>(endpoint, {
    authToken,
    searchParams,
  })

  const etag = response.etag ?? undefined
  const rawItems = response.data.items ?? response.data.objects ?? []
  const transformedItems = rawItems.map(obj => transformObject(obj, options?.conversationId, etag)) || []

  return {
    ...response.data,
    items: transformedItems,
  }
}
